import React, { useState, useEffect, useCallback } from 'react';
import { withTranslation } from '../../../ChildrenContext.js';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DropdownList from 'react-widgets/DropdownList';
import FormGroup from '../../../FormGroup';
import Button from '../../../Button';
import ProfileAccountSettingsComponent from '../ProfileAccountSettings/ProfileAccountSettings.container';
import CustomModal from "../../../Modal";
import CustomTooltip from '../../../Tooltip.js';

const ProfilePersonalInformation = ({
    loading: initialLoading,
    nickname,
    id,
    name,
    surname,
    anonymous,
    email,
    timezone,
    timezonesList,
    subscribed,
    rank,
    ranks,
    contactTypes,
    contacts,
    settingsSave,
    t
}) => {
    const [state, setState] = useState({
        loading: initialLoading,
        nickname: nickname,
        id: id,
        name: name,
        surname: surname,
        anonymous: anonymous,
        email: email,
        timezone: timezone || '',
        timezonesList: timezonesList || [],
        subscribed: subscribed,
        rank: rank,
        ranks: ranks,
        contactTypes: contactTypes,
        contacts: contacts,
        dataCurRank: {},
        errors: {},
        modalIsVisible: false,
        selectedContactType: null,
        addContactType: null,
        addContactValue: null,
        disabledConfirmButton: true
    });

    const [hasContactChanges, setHasContactChanges] = useState(false);

    const setCurRank = useCallback(() => {
        const data = state.ranks.find((item) => item.rank === state.rank) || {};
        setState(prevState => ({
            ...prevState,
            dataCurRank: data
        }));
    }, [state.ranks, state.rank]);

    const onChangeField = useCallback((event) => {
        const { contacts } = state;
        const index = event.target.closest('.contacts').getAttribute('data-index');
        const value = event.target.value;

        setState(prevState => ({
            ...prevState,
            contacts: (contacts || []).map((item, i) => ({
                ...item,
                value: i === Number(index) ? value : item.value
            }))
        }));
    }, [state.contacts]);

    const onChangeContactValue = useCallback((e, index) => {
        const { contacts } = state;
        let contactType = e.target.getAttribute('data-type');
        let value = e.target.value;

        setState(prevState => ({
            ...prevState,
            contacts: (contacts || []).map((item, i) => ({
                ...item,
                value: item.type === contactType && i === index ? value : item.value
            }))
        }));
    }, [state.contacts]);

    const onAddContactValue = useCallback((e) => {
        let type = state.addContactType;
        let value = e.target.value;

        setState(prevState => ({
            ...prevState,
            disabledConfirmButton: 0 >= value.length || null === type,
            addContactValue: value
        }));
    }, [state.addContactType]);

    const onAddContactType = useCallback((type) => {
        setState(prevState => ({
            ...prevState,
            disabledConfirmButton: !prevState.addContactValue,
            addContactType: type
        }));
    }, [state.addContactValue]);

    const onAddContact = useCallback(() => {
        let contactType = state.addContactType;
        let value = state.addContactValue;
        const contactsList = state.contacts ? [...state.contacts] : [];
        const placeholderIndex = contactsList.findIndex((item) => item.type === "" && item.value === "");

        if (placeholderIndex !== -1) {
            contactsList[placeholderIndex] = {
                ...contactsList[placeholderIndex],
                type: contactType.value,
                value
            };
        } else {
            contactsList.push({
                type: contactType.value,
                value
            });
        }
        setHasContactChanges(true);
        setState(prevState => ({
            ...prevState,
            modalIsVisible: false,
            disabledConfirmButton: true,
            addContactType: null,
            addContactValue: null,
            contacts: contactsList
        }));
    }, [state.addContactType, state.addContactValue, state.contacts]);

    const onAddContactModal = useCallback(() => {
        const contacts = [...state.contacts];
        contacts.push({ type: "", value: ""});

        setState(prevState => ({
            ...prevState,
            contacts
        }));

        showModal();
    }, [state.contacts]);

    const showModal = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            addContactValue: null,
            addContactType: null,
            disabledConfirmButton: true,
            modalIsVisible: true
        }));
    }, []);

    const closeModal = useCallback(() => {
        const { contacts } = state;
        const selectedType = state.selectedContactType;

        let items = contacts.filter((item, i) => {
            return '' !== item.type && '' !== item.value || selectedType && item.type !== selectedType.value;
        });

        setState(prevState => ({
            ...prevState,
            disabledConfirmButton: true,
            modalIsVisible: false,
            addContactValue: null,
            addContactType: null,
            contacts: items
        }));
    }, [state.contacts, state.selectedContactType]);

    const onDeleteContact = useCallback((e) => {
        const { contacts } = state;
        let index = e.target.getAttribute('data-index');
        let items = contacts.filter((item, i) => i !== parseInt(index));

        setState(prevState => ({
            ...prevState,
            contacts: items
        }));
        setHasContactChanges(true);
    }, [state.contacts]);

    const onChangeAnonymous = useCallback((event) => {
        setState(prevState => ({
            ...prevState,
            anonymous: !event.target.checked
        }));
    }, []);

    const onChangeSubscribed = useCallback((event) => {
        setState(prevState => ({
            ...prevState,
            subscribed: event.target.checked
        }));
    }, []);

    const onChangeTimezone = useCallback((item) => {
        setState(prevState => ({
            ...prevState,
            timezone: item.value
        }));
    }, []);

    const handleSettingsSave = useCallback((event) => {
        if (state.loading) return;

        event.preventDefault();

        settingsSave(state, t, onSuccess, onError);
        setState(prevState => ({
            ...prevState,
            loading: true
        }));
        setHasContactChanges(false);
    }, [state, settingsSave, t]);

    const onSuccess = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            loading: false,
            errors: {},
            modalIsVisible: false
        }));
    }, []);

    const onError = useCallback((errors) => {
        setState(prevState => ({
            ...prevState,
            loading: false,
            errors
        }));
    }, []);

    const getTypeTitle = useCallback((type) => {
        const { contactTypes } = state;
        const { title = '' } = contactTypes.find(item => item.value === type) || {};
        return title;
    }, [state.contactTypes]);

    const isHaveErrorContact = useCallback((index, type) => {
        const { errors: { contacts = {} } } = state;
        return !!contacts[`[${index}][${type}]`];
    }, [state.errors]);

    const renderErrorContact = useCallback((index, type) => {
        const { errors: { contacts = {} } } = state;

        if (contacts[`[${index}][${type}]`]) {
            return <span className="error-message help-block">{contacts[`[${index}][${type}]`]}</span>;
        }

        return null;
    }, [state.errors]);

    useEffect(() => {
        setCurRank();
    }, [setCurRank]);

    useEffect(() => {
        setState(prevState => ({
            ...prevState,
            nickname: nickname,
            id: id,
            anonymous: anonymous,
            subscribed: subscribed,
            rank: rank,
            ranks: ranks,
            contactTypes: contactTypes,
            contacts: contacts,
            name: name,
            surname: surname,
            email: email,
            timezone: timezone || '',
            timezonesList: timezonesList || [],
        }));
    }, [nickname, id, anonymous, subscribed, rank, ranks, contactTypes, contacts, name, surname, email, timezone, timezonesList]);

    useEffect(() => {
        setCurRank();
    }, [setCurRank]);

    const staticNames = t?.profilePage?.personalInformation || {};
    const staticNamesActions = t?.actions || {};

    return (
        <form className="form-personal-info form" onSubmit={handleSettingsSave}>
            <div className="form-body">
                <div className="form-body__info">
                    <FormGroup
                        label={`${staticNames.name}:`}
                        errors={state.errors.name}
                        className={{"name-form": true}}
                    >
                        <input
                            className="form-control"
                            type="text"
                            name="name"
                            value={state.name}
                            readOnly={true}
                        />
                    </FormGroup>
                    <FormGroup
                        label={`${staticNames.surname}:`}
                        errors={state.errors.surname}
                        className={{"name-form": true}}
                    >
                        <input
                            className="form-control"
                            type="text"
                            name="surname"
                            value={state.surname}
                            readOnly={true}
                        />
                    </FormGroup>
                    <FormGroup
                        label={`${staticNames.email}:`}
                        errors={state.errors.email}
                        className={{"name-form": true}}
                    >
                        <input
                            className="form-control"
                            type="text"
                            name="email"
                            value={state.email}
                            readOnly={true}
                        />
                    </FormGroup>
                    <FormGroup
                        label={`${staticNames.timezone}:`}
                        errors={state.errors.timezone}
                        className={{"timezone-form": true}}
                    >
                        <DropdownList
                            className={'form-control'}
                            dataKey="title"
                            textField="title"
                            data={state.timezonesList}
                            value={state.timezone}
                            filter="contains"
                            onChange={onChangeTimezone}
                        />
                    </FormGroup>
                </div>
                <div className="form-body__settings">
                    <FormGroup
                        label={`${staticNames.idForTop}:`}
                        errors={state.errors.id}
                        className={{"name-form": true}}
                    >
                        <input
                            className="form-control short-imput"
                            type="text"
                            name="id"
                            value={state.id}
                            required
                            readOnly={true}
                        />
                    </FormGroup>
                    <ProfileAccountSettingsComponent />
                    <FormGroup className={{ "news-form": true}}>
                        <label className="option">
                            <input
                                className="option__input"
                                type="checkbox"
                                name="subscribed"
                                checked={state.subscribed}
                                onChange={onChangeSubscribed}
                            />
                            <span className="option__mark profile_mark" />
                            <span className="option__caption"> {staticNames.getNews}</span>
                        </label>
                    </FormGroup>
                </div>
                <div className="form-body__contacts">
                    <p className="form-body__contacts-header">{staticNames.contactsTitle}</p>

                    <CustomModal
                        visible={state.modalIsVisible}
                        onClose={closeModal}
                        onConfirm={onAddContact}
                        loading={state.loading}
                        title={staticNames.addContact}
                        confirmDisabled={state.disabledConfirmButton}
                    >
                        <div className="form-horizontal">
                            <FormGroup>
                                <div>
                                    <div className={classNames({
                                        'has-error': isHaveErrorContact(1, 'type')
                                    })}>
                                        <DropdownList
                                            className={'rw-dropdownlist-select contact-type'}
                                            dataKey="value"
                                            textField="title"
                                            placeholder={staticNames.selectType}
                                            data={state.contactTypes}
                                            onChange={(value) => onAddContactType(value)}
                                        />
                                        {renderErrorContact(1, 'type')}
                                        <div className={classNames({
                                            'contact-number': true,
                                            'has-error': isHaveErrorContact(1, 'value')
                                        })}>
                                            <input
                                                className="form-control short-input"
                                                type="text"
                                                name="contactValue"
                                                onChange={onAddContactValue}
                                                data-type={state.selectedContactType}
                                                required={true}
                                            />
                                            {renderErrorContact(1, 'value')}
                                        </div>

                                    </div>
                                </div>
                            </FormGroup>
                        </div>
                    </CustomModal>

                    {(state.contacts || []).map((item, index) => (
                        <div key={index}>
                            <div className={classNames({'has-error': isHaveErrorContact(index, 'type')})}>
                                <span className="contact__title">{item.type}</span>
                                {renderErrorContact(index, 'type')}
                                <div className={classNames({'contact-number': true, 'has-error': isHaveErrorContact(index, 'value')})}>
                                    <input
                                        className="form-control short-input"
                                        type="text"
                                        name="contactValue"
                                        data-type={item.type}
                                        value={item.value}
                                        onChange={(value) => onChangeContactValue(value, index)}
                                    />
                                    <CustomTooltip id={`contact-delete-${index}`} tooltip={t?.actions?.delete}>
                                        <div
                                            className="contact-number__delete"
                                            onClick={onDeleteContact}
                                            data-index={index}
                                        >
                                        </div>
                                    </CustomTooltip>
                                    {renderErrorContact(index, 'value')}
                                </div>
                            </div>
                        </div>
                    ))}

                    {hasContactChanges ? <div>{staticNames.changesSaveReminder}</div> : ''}

                    <div className="form-body__contacts-add">
                        <Button
                            onClick={onAddContactModal}
                            extraClass={{addButton: true}}
                            text={staticNames.addContact}
                        />
                    </div>
                </div>
            </div>
            <div className="form-footer">
                <FormGroup fieldClass={'save-form'}>
                    <Button
                        extraClass={{"btn apply-btn": true}}
                        loading={state.loading}
                        text={staticNamesActions.save}
                        type="submit"
                    />
                </FormGroup>
            </div>
        </form>
    );
};

ProfilePersonalInformation.propTypes = {
    loading: PropTypes.bool,
    nickname: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    surname: PropTypes.string,
    anonymous: PropTypes.bool,
    email: PropTypes.string,
    timezone: PropTypes.string,
    timezonesList: PropTypes.array,
    subscribed: PropTypes.bool,
    rank: PropTypes.number,
    ranks: PropTypes.array,
    contactTypes: PropTypes.array,
    contacts: PropTypes.array,
    settingsSave: PropTypes.func,
    t: PropTypes.object
};

ProfilePersonalInformation.defaultProps = {
    loading: false,
    nickname: '',
    id: '',
    name: '',
    surname: '',
    anonymous: true,
    email: '',
    timezone: '',
    timezonesList: [],
    subscribed: true,
    rank: 0,
    ranks: [],
    contactTypes: [],
    contacts: [],
    settingsSave: () => {}
};

export default withTranslation(ProfilePersonalInformation);
